import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { FieldShell, NativeSelect } from "./crm-ui";
import { useCrmText, type CrmTextKey } from "@/lib/crm/i18n";
import { fieldDef, fieldLabel, fieldsOf, optionLabel, type CrmEntity } from "@/lib/crm/fields";
import type { CrmConditionOp, CrmMember, CrmViewColumn, CrmViewCondition, CrmViewFilter, CrmViewSort } from "@/lib/crm/api";
import { ArrowDown, ArrowUp, Loader2, Plus, X } from "lucide-react";

export interface ViewDraft {
  name: string;
  isShared: boolean;
  columns: CrmViewColumn[];
  filter: CrmViewFilter;
  sort: CrmViewSort;
}

interface Props {
  entity: CrmEntity;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  initial: ViewDraft;
  /** System views are company-wide: no share toggle. */
  isSystem: boolean;
  members: CrmMember[];
  onSave: (draft: ViewDraft) => Promise<void>;
}

const OPS: CrmConditionOp[] = ["eq", "neq", "contains", "notcontains", "empty", "notempty", "gt", "lt"];

export function CrmViewEditor({ entity, open, onOpenChange, title, initial, isSystem, members, onSave }: Props) {
  const { lang, tx } = useCrmText();
  const [draft, setDraft] = useState<ViewDraft>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Start from `initial` each time the dialog opens (not on every parent render).
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setDraft({ ...initial, columns: initial.columns.map((c) => ({ ...c })), filter: { ...initial.filter, conditions: initial.filter.conditions.map((c) => ({ ...c })) } });
      setError(null);
    }
    wasOpen.current = open;
  }, [open, initial]);

  const all = fieldsOf(entity);
  const label = (k: string) => fieldLabel(entity, k, lang);
  const unused = all.filter((f) => !draft.columns.some((c) => c.key === f.key));

  const setFilter = (patch: Partial<CrmViewFilter>) => setDraft((d) => ({ ...d, filter: { ...d.filter, ...patch } }));
  const setCondition = (i: number, patch: Partial<CrmViewCondition>) =>
    setFilter({ conditions: draft.filter.conditions.map((c, j) => (j === i ? { ...c, ...patch } : c)) });

  const moveColumn = (i: number, dir: -1 | 1) => setDraft((d) => {
    const cols = [...d.columns];
    const j = i + dir;
    if (j < 0 || j >= cols.length) return d;
    [cols[i], cols[j]] = [cols[j], cols[i]];
    return { ...d, columns: cols };
  });

  const save = async () => {
    if (!draft.name.trim()) { setError(tx("needName")); return; }
    if (draft.columns.length === 0) { setError(tx("needColumn")); return; }
    setSaving(true);
    setError(null);
    try {
      const sortKey = draft.sort.key || draft.columns[0].key;
      await onSave({
        ...draft,
        name: draft.name.trim(),
        sort: { key: sortKey, dir: draft.sort.dir },
        filter: { ...draft.filter, conditions: draft.filter.conditions.filter((c) => c.field) },
      });
      onOpenChange(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const valueInput = (c: CrmViewCondition, i: number) => {
    if (c.op === "empty" || c.op === "notempty") return <div className="hidden sm:block" />;
    const def = fieldDef(entity, c.field);
    if (def?.type === "option" || def?.type === "boolean" || def?.type === "user") {
      const options = def.type === "option"
        ? (def.options || []).map((o) => ({ v: o, l: optionLabel(def.key, o, lang) }))
        : def.type === "boolean"
          ? [{ v: "true", l: tx("yes") }, { v: "false", l: tx("no") }]
          : members.map((m) => ({ v: m.userId, l: m.name }));
      return (
        <NativeSelect value={c.value || ""} onChange={(e) => setCondition(i, { value: e.target.value })} data-testid={`view-cond-value-${i}`}>
          <option value="">{tx("select")}</option>
          {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
        </NativeSelect>
      );
    }
    return (
      <Input
        type={def?.type === "datetime" ? "date" : def?.type === "number" || def?.type === "decimal" ? "number" : "text"}
        value={c.value || ""}
        onChange={(e) => setCondition(i, { value: e.target.value })}
        placeholder={tx("value")}
        data-testid={`view-cond-value-${i}`}
      />
    );
  };

  const activeLabel = entity === "lead" ? tx("stateActiveLead") : tx("stateActiveContact");
  const inactiveLabel = entity === "lead" ? tx("stateInactiveLead") : tx("stateInactiveContact");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="view-editor">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{isSystem ? tx("systemViewHint") : draft.isShared ? tx("shareViewHint") : tx("myViews")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
            <FieldShell label={tx("viewName")} required>
              <Input value={draft.name} maxLength={100} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="view-name" />
            </FieldShell>
            {!isSystem && (
              <label className="flex h-9 items-center gap-2 text-sm">
                <Switch checked={draft.isShared} onCheckedChange={(v) => setDraft({ ...draft, isShared: v })} data-testid="view-shared" />
                {tx("shareView")}
              </label>
            )}
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">{tx("columns")}</p>
            <ul className="space-y-1">
              {draft.columns.map((c, i) => (
                <li key={c.key} className="flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm" data-testid={`view-column-${c.key}`}>
                  <span className="w-6 text-xs text-muted-foreground">{i + 1}</span>
                  <span className="min-w-0 flex-1 truncate">{label(c.key)}</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7" title={tx("moveUp")} onClick={() => moveColumn(i, -1)} disabled={i === 0}><ArrowUp className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" title={tx("moveDown")} onClick={() => moveColumn(i, 1)} disabled={i === draft.columns.length - 1}><ArrowDown className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" title={tx("removeField")} onClick={() => setDraft({ ...draft, columns: draft.columns.filter((x) => x.key !== c.key) })}
                    disabled={draft.columns.length <= 1} data-testid={`view-remove-column-${c.key}`}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
            {unused.length > 0 && draft.columns.length < 20 && (
              <NativeSelect value="" onChange={(e) => { if (e.target.value) setDraft({ ...draft, columns: [...draft.columns, { key: e.target.value }] }); }}
                className="w-64" data-testid="view-add-column">
                <option value="">{tx("addColumn")}</option>
                {unused.map((f) => <option key={f.key} value={f.key}>{label(f.key)}</option>)}
              </NativeSelect>
            )}
          </div>

          <div className="space-y-3">
            <p className="text-sm font-medium">{tx("filters")}</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <FieldShell label={tx("stateFilter")}>
                <NativeSelect value={draft.filter.state} onChange={(e) => setFilter({ state: e.target.value as CrmViewFilter["state"] })} data-testid="view-state">
                  <option value="active">{activeLabel}</option>
                  <option value="inactive">{inactiveLabel}</option>
                  <option value="all">{tx("stateAll")}</option>
                </NativeSelect>
              </FieldShell>
              <FieldShell label={tx("ownerFilter")}>
                <NativeSelect value={draft.filter.owner} onChange={(e) => setFilter({ owner: e.target.value as CrmViewFilter["owner"] })} data-testid="view-owner">
                  <option value="any">{tx("ownerAny")}</option>
                  <option value="me">{tx("ownerMe")}</option>
                </NativeSelect>
              </FieldShell>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">{tx("conditions")}</p>
              {draft.filter.conditions.map((c, i) => (
                <div key={i} className="grid items-center gap-2 sm:grid-cols-[1fr_10rem_1fr_auto]" data-testid={`view-cond-${i}`}>
                  <NativeSelect value={c.field} onChange={(e) => setCondition(i, { field: e.target.value, value: "" })} data-testid={`view-cond-field-${i}`}>
                    <option value="">{tx("select")}</option>
                    {all.map((f) => <option key={f.key} value={f.key}>{label(f.key)}</option>)}
                  </NativeSelect>
                  <NativeSelect value={c.op} onChange={(e) => setCondition(i, { op: e.target.value as CrmConditionOp })} data-testid={`view-cond-op-${i}`}>
                    {OPS.map((op) => <option key={op} value={op}>{tx(`op_${op}` as CrmTextKey)}</option>)}
                  </NativeSelect>
                  {valueInput(c, i)}
                  <Button variant="ghost" size="icon" className="h-8 w-8" title={tx("delete")}
                    onClick={() => setFilter({ conditions: draft.filter.conditions.filter((_, j) => j !== i) })}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {draft.filter.conditions.length < 20 && (
                <Button variant="outline" size="sm" onClick={() => setFilter({ conditions: [...draft.filter.conditions, { field: "", op: "contains", value: "" }] })}
                  data-testid="view-add-condition">
                  <Plus className="h-4 w-4" /> {tx("addCondition")}
                </Button>
              )}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <FieldShell label={tx("sortBy")}>
              <NativeSelect value={draft.sort.key} onChange={(e) => setDraft({ ...draft, sort: { ...draft.sort, key: e.target.value } })} data-testid="view-sort-key">
                {all.map((f) => <option key={f.key} value={f.key}>{label(f.key)}</option>)}
              </NativeSelect>
            </FieldShell>
            <FieldShell label={tx("direction")}>
              <NativeSelect value={draft.sort.dir} onChange={(e) => setDraft({ ...draft, sort: { ...draft.sort, dir: e.target.value as "asc" | "desc" } })} data-testid="view-sort-dir">
                <option value="asc">{tx("asc")}</option>
                <option value="desc">{tx("desc")}</option>
              </NativeSelect>
            </FieldShell>
          </div>
        </div>

        {error && <p className="text-sm text-destructive" role="alert">{error}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{tx("cancel")}</Button>
          <Button onClick={save} disabled={saving} data-testid="view-save">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} {tx("save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
